import { useEffect, useRef, useState } from "react";
import { getStoredToken } from "../../utils/impersonation.utils";
import type { PreVisitBriefData } from "./widgets/PreVisitBrief";

// Pre-charting REST calls (backend `prechart/`, docs/precharting/api-contract.md)
// and the on-open trigger that posts the pre-visit brief into the Superdoc
// thread. Every failure here is silent: the brief is a bonus on top of the
// chat, never something that may break or slow it.

export type PrechartStatus =
  | "disabled"
  | "none"
  | "queued"
  | "collecting"
  | "extracting"
  | "synthesizing"
  | "ready"
  | "partial"
  | "failed";

export interface PrechartBriefResponse {
  enabled: boolean;
  status: PrechartStatus;
  run_id?: string | null;
  brief?: PreVisitBriefData | null;
  shown_in_thread?: boolean;
}

export type PrechartRefreshResult = "queued" | "in_progress" | "disabled" | "error";

export type PreVisitBriefAction = { type: "pre_visit_brief"; payload: { brief_id: string } };

export const prechartBaseUrl = (): string => process.env.REACT_APP_SERVER_URL || "";

// The same headers the Superdoc runtime sends on its own REST calls.
export const prechartHeaders = (clientId?: number): Record<string, string> => {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  const token = getStoredToken();
  if (token) headers["Authorization"] = `Token ${token}`;
  if (clientId) headers["X-Client-ID"] = clientId.toString();
  return headers;
};

export async function fetchPrechartBrief(baseUrl: string, clientId: number): Promise<PrechartBriefResponse | null> {
  const res = await fetch(`${baseUrl}/prechart/brief/?client_id=${encodeURIComponent(String(clientId))}`, {
    headers: prechartHeaders(clientId),
  });
  if (!res.ok) return null;
  return (await res.json()) as PrechartBriefResponse;
}

export async function requestPrechartRefresh(baseUrl: string, clientId: number): Promise<PrechartRefreshResult> {
  try {
    const res = await fetch(`${baseUrl}/prechart/refresh/`, {
      method: "POST",
      headers: prechartHeaders(clientId),
      body: JSON.stringify({ client_id: clientId }),
    });
    if (res.ok) return "queued";
    if (res.status === 409) return "in_progress";
    if (res.status === 403) return "disabled";
    return "error";
  } catch (err) {
    console.debug("[Superdoc] pre-chart refresh failed", err);
    return "error";
  }
}

export async function sendPrechartFeedback(
  baseUrl: string,
  briefId: string,
  helpful: boolean,
  clientId?: number,
): Promise<boolean> {
  try {
    const res = await fetch(`${baseUrl}/prechart/brief/${encodeURIComponent(briefId)}/feedback/`, {
      method: "POST",
      headers: prechartHeaders(clientId),
      body: JSON.stringify({ helpful, comment: "" }),
    });
    return res.ok;
  } catch (err) {
    console.debug("[Superdoc] pre-chart feedback failed", err);
    return false;
  }
}

/**
 * The brief to post into the thread, or null: only a finished brief
 * (ready / partial) for a clinician who has pre-charting on, and only when the
 * thread doesn't already carry this version.
 */
export const briefIdToAnnounce = (res: PrechartBriefResponse | null | undefined): string | null => {
  if (!res?.enabled) return null;
  if (res.status !== "ready" && res.status !== "partial") return null;
  if (res.shown_in_thread) return null;
  return res.brief?.brief_id || null;
};

interface UsePreVisitBriefTriggerOptions {
  baseUrl: string;
  clientId?: number;
  // The thread's own history has landed. Firing earlier would stream the
  // brief into the thread and then have the history load overwrite it.
  ready: boolean;
  fire: (action: PreVisitBriefAction) => unknown;
}

/**
 * On Superdoc mount for a patient: ask once whether a pre-visit brief is
 * waiting and, if so, fire the `pre_visit_brief` custom action once. The panel
 * remounts per patient (`key={activeClientId}`), so "once" is per mount; the
 * lookup runs in parallel with everything else and never holds up the
 * post-encounter review trigger.
 */
export function usePreVisitBriefTrigger({ baseUrl, clientId, ready, fire }: UsePreVisitBriefTriggerOptions) {
  const fireRef = useRef(fire);
  fireRef.current = fire;
  const mountedRef = useRef(false);
  const requestedForRef = useRef<number | null>(null);
  const firedRef = useRef<Set<string>>(new Set());
  const [pendingBriefId, setPendingBriefId] = useState<string | null>(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    // One lookup per patient per mount — also under StrictMode's re-run.
    if (!clientId || requestedForRef.current === clientId) return;
    requestedForRef.current = clientId;
    fetchPrechartBrief(baseUrl, clientId)
      .then((res) => {
        const briefId = briefIdToAnnounce(res);
        if (briefId && mountedRef.current) setPendingBriefId(briefId);
      })
      .catch((err) => console.debug("[Superdoc] pre-visit brief lookup failed", err));
  }, [baseUrl, clientId]);

  useEffect(() => {
    if (!ready || !pendingBriefId || firedRef.current.has(pendingBriefId)) return;
    firedRef.current.add(pendingBriefId);
    try {
      Promise.resolve(fireRef.current({ type: "pre_visit_brief", payload: { brief_id: pendingBriefId } })).catch(
        (err) => console.debug("[Superdoc] pre-visit brief action failed", err),
      );
    } catch (err) {
      console.debug("[Superdoc] pre-visit brief action failed", err);
    }
  }, [ready, pendingBriefId]);
}
